import { create } from "zustand"
import { DOC_STATUSES, type DocRow, type DocStatus } from "./contract"
import type { StreamEvent } from "./stream"

export interface DocView {
  filename: string
  status: DocStatus
  pagesTotal: number
  pagesDone: number
  pagesFailed: number
  summary?: Record<string, unknown>
  error?: string
}

const blank = (): DocView => ({ filename: "", status: "queued", pagesTotal: 0, pagesDone: 0, pagesFailed: 0 })

/** Pure reducer for the document event stream. */
export function reduceDocs(docs: Record<string, DocView>, event: StreamEvent): Record<string, DocView> {
  const p = event.payload as Record<string, any> // eslint-disable-line @typescript-eslint/no-explicit-any
  const id = p.doc_id as string | undefined
  if (!id) return docs
  const cur = docs[id] ?? blank()
  switch (event.type) {
    case "doc.progress": {
      const known = (DOC_STATUSES as readonly string[]).includes(p.status)
      return {
        ...docs,
        [id]: {
          ...cur, filename: p.filename, status: known ? (p.status as DocStatus) : cur.status, pagesTotal: p.pages_total,
          pagesDone: p.pages_done, pagesFailed: p.pages_failed,
        },
      }
    }
    case "doc.ready":
      return { ...docs, [id]: { ...cur, status: "ready", summary: p.summary } }
    case "doc.error":
      return { ...docs, [id]: { ...cur, status: "failed", error: p.message } }
    default:
      return docs
  }
}

interface DocsStore {
  docs: Record<string, DocView>
  apply: (event: StreamEvent) => void
  setRows: (rows: DocRow[]) => void
  addUploads: (rows: DocRow[]) => void
}

export const useDocsStore = create<DocsStore>((set) => ({
  docs: {},
  apply: (event) => set((s) => ({ docs: reduceDocs(s.docs, event) })),
  setRows: (rows) =>
    set((s) => {
      const docs = { ...s.docs }
      for (const r of rows) {
        docs[r.doc_id] = { ...(docs[r.doc_id] ?? blank()), filename: r.filename, status: r.status, error: r.error ?? undefined }
      }
      return { docs }
    }),
  /** Upload responses are snapshots taken before progress may already have arrived: never overwrite a known doc. */
  addUploads: (rows) =>
    set((s) => {
      const docs = { ...s.docs }
      for (const r of rows) {
        if (docs[r.doc_id]) {
          docs[r.doc_id] = { ...docs[r.doc_id], filename: r.filename }
          continue
        }
        docs[r.doc_id] = { ...blank(), filename: r.filename, status: r.status, error: r.error ?? undefined }
      }
      return { docs }
    }),
}))
